// Shared handling for the bulk-import listing pages' multi-source fan-out. Both pages fetch
// one request per accessible microservice source and join the results into a single list. With
// more than one source a single unavailable microservice must not blank the whole listing, so
// the results are settled rather than all-or-nothing: the sources that answered are rendered
// and the ones that failed are named to the user.

import { UserHasAccessActions } from 'components/user-access';

import { importTypes } from './bulkImportTypes';

const badRequestCode = 400;

// The display names of the import types a microservice source hosts, limited to those the user
// holds the claim for. A failure names the types the user recognises from the New Import
// dropdown rather than the internal source key, so a type they have no claim for must not be
// named — the asset source hosts both Asset (Fleet:Import) and Asset Location (Internal), and a
// user without Internal has never seen the latter. A source is only ever fetched when the user
// holds one of its types' claims, so at least one name always remains.
function getSourceTypeNames(sourceKey, userActions) {
    return importTypes
        .filter((type) => {
            return !type.isLegacy
                && type.microserviceKey === sourceKey
                && UserHasAccessActions([type.requiredClaim], userActions);
        })
        .map((type) => type.typeName);
}

// Splits settled fan-out results into the merged rows of the sources that answered and the
// failures of those that did not. Failures keep their source and rejection reason, and stay in
// the order the sources were requested so the caller can report the first one deterministically.
export function splitSettledSources(sources, settledResults) {
    const rows = [];
    const failures = [];

    settledResults.forEach((result, index) => {
        if (result.status === "fulfilled") {
            rows.push(...result.value);
        } else {
            failures.push({ source: sources[index], reason: result.reason });
        }
    });

    return { rows: rows, failures: failures };
}

// The persistent warning shown when some — but not all — sources failed. Each failed source
// contributes a sentence: the API's own message when it rejected with a 400 that carries one,
// otherwise a generic sentence naming the source's import types. The closing sentence tells the
// user the rendered list is partial, which holds whatever the individual failure reasons were.
// The subject is the listing's noun ("imports" or "mappings").
export function buildFailureWarningText(failures, subject, userActions) {
    const sentences = failures.map((failure) => {
        const validationMessage = failure.reason?.status === badRequestCode ? failure.reason?.data : null;
        if (validationMessage != null && validationMessage !== "") {
            return validationMessage;
        }

        return `${getSourceTypeNames(failure.source.sourceKey, userActions).join(", ")} ${subject} could not be loaded.`;
    });

    return `${sentences.join(" ")} The list below may be incomplete.`;
}
